import { useEffect, useState } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { AdminTenant, apiService } from '../services/api';
import { useTenantFilter } from '../contexts/TenantFilterContext';
import { DIRECT_CUSTOMERS_SELECTION, type TenantSelection } from '../contexts/tenantSelection';

const PICKER_PAGE_SIZE = 10;

/**
 * Globale Tenant-Auswahl im Header (nur Super-Admins). Tenants werden
 * backendseitig gesucht und paginiert; die Auswahl filtert u.a. die Kundenliste.
 */
export function TenantSelector() {
  const tenantFilter = useTenantFilter();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setLoading(true);
      setLoadError(false);
      apiService
        .getAdminTenants({ search: search.trim() || undefined, page, limit: PICKER_PAGE_SIZE })
        .then((result) => {
          setTenants(result.data);
          setTotalPages(result.pagination.totalPages || 1);
        })
        .catch(() => { setLoadError(true); })
        .finally(() => { setLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); };
  }, [open, search, page]);

  if (!tenantFilter.enabled) return null;
  const { selectedTenant, setSelectedTenant } = tenantFilter;

  const choose = (selection: TenantSelection): void => {
    setSelectedTenant(selection);
    setOpen(false);
  };

  const renderOption = (label: string, selection: TenantSelection, hint?: string) => {
    const isSelected = selectedTenant.id === selection.id;
    return (
      <button
        type="button"
        onClick={() => { choose(selection); }}
        className="w-full text-left px-2 py-1.5 rounded-sm text-sm hover:bg-gray-100 flex items-center gap-2"
      >
        <Check className={`w-4 h-4 shrink-0 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
        <span className="truncate">{label}</span>
        {hint && <span className="ml-auto text-xs text-gray-400 shrink-0">{hint}</span>}
      </button>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch('');
          setPage(1);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="max-w-[240px] border-blue-300 bg-blue-50 text-blue-800"
          aria-label="Tenant auswählen"
        >
          <Building2 className="w-4 h-4 mr-2 shrink-0" />
          <span className="truncate">{selectedTenant.name}</span>
          <ChevronDown className="w-4 h-4 ml-2 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="space-y-2">
          {renderOption(DIRECT_CUSTOMERS_SELECTION.name, DIRECT_CUSTOMERS_SELECTION, 'Callbert')}
          <div className="border-t pt-2">
            <Input
              placeholder="Tenant suchen…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              autoFocus
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="py-4 text-center text-xs text-gray-500">Lade Tenants…</div>
            ) : loadError ? (
              <div className="py-4 text-center text-xs text-red-600">Tenants konnten nicht geladen werden.</div>
            ) : tenants.length === 0 ? (
              <div className="py-4 text-center text-xs text-gray-500">Keine Tenants gefunden.</div>
            ) : (
              tenants.map((tenant) => (
                <div key={tenant.id}>
                  {renderOption(tenant.name, { id: tenant.id, name: tenant.name }, tenant.isActive ? undefined : 'pausiert')}
                </div>
              ))
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => { setPage((p) => p - 1); }}
              >
                Zurück
              </Button>
              <span className="text-xs text-gray-500">
                Seite {page} von {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => { setPage((p) => p + 1); }}
              >
                Weiter
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
